import { ReportSections } from './report-schedule.types';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ReportTimeOption {
  value: string;
  label: string;
}

/** Each hour from 9am through 5pm, stored as HH:00. */
export function reportTimeOptions(): ReportTimeOption[] {
  const options: ReportTimeOption[] = [];
  for (let hour = 9; hour <= 17; hour += 1) {
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    const suffix = hour < 12 ? 'am' : 'pm';
    options.push({
      value: `${String(hour).padStart(2, '0')}:00`,
      label: `${hour12}${suffix}`,
    });
  }
  return options;
}

/** Map a saved time onto an offered hour so one button stays selected. */
export function coerceReportHour(time: string): string {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!match) {
    return '09:00';
  }
  const hour = Number(match[1]);
  if (hour < 9) {
    return '09:00';
  }
  if (hour > 17) {
    return '17:00';
  }
  return `${String(hour).padStart(2, '0')}:00`;
}

export function dedupeRecipientEmails(recipients: string[]): { emails: string[]; error: string | null } {
  const emails: string[] = [];
  const seen = new Set<string>();
  for (const raw of recipients) {
    const email = raw.trim().toLowerCase();
    if (!email || seen.has(email)) {
      continue;
    }
    if (!EMAIL_PATTERN.test(email)) {
      return { emails, error: 'Enter a valid email address' };
    }
    seen.add(email);
    emails.push(email);
  }
  if (emails.length > 20) {
    return { emails: emails.slice(0, 20), error: 'A schedule can have at most 20 recipients' };
  }
  if (!emails.length) {
    return { emails, error: 'Add at least one recipient' };
  }
  return { emails, error: null };
}

export function hasReportSection(sections: ReportSections): boolean {
  return sections.overview || sections.stats || sections.topPerformers || sections.posts;
}

export function reportBannerMode(schedule: { isActive: boolean } | null | undefined): 'scheduled' | 'unscheduled' {
  return schedule?.isActive ? 'scheduled' : 'unscheduled';
}

export function schedulePillLabel(dayOfWeek: number | null, timeOfDay: string | null): string {
  if (!dayOfWeek || !timeOfDay) {
    return 'Schedule report';
  }
  const short = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][dayOfWeek] ?? 'Schedule report';
  return `${short} · ${timeOfDay}`;
}

export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function ianaTimeZones(): string[] {
  const supported = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
  if (typeof supported.supportedValuesOf !== 'function') {
    return [browserTimeZone()];
  }
  return supported.supportedValuesOf('timeZone');
}
